/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 9/7/21.
 */

import * as _ from "lodash";
import HotKeysApi from "@/services/HotKeys";
import PlaylistApi from "@/services/Playlist";
import AssetApi from "@/services/Asset";

const defaultState = () => {
	return {
		pages: [],
		selectedPageId: null,
		hotkeyLoading: []
	}
}

const defaultPageState = () => {
	return {
		Rows: 4,
		Columns: 3,
		HotKeys: {
			data: [],
			loaded: false
		},
		locked: false,
		hotKeysRowsPerSoundCard: {}
	}
}

// initial state
const state = {
	...defaultState(),
	selectedPage: defaultPageState(),
	outputType: {},
	gain: {}
};

const getters = {
	getPages: (state) => () => {
		return state.pages;
	},

	selectedPageId: (state) => () => {
		return state.selectedPageId;
	},

	getSelectedPageData: (state) => () => {
		return state.selectedPage;
	},

	getPageById: (state, getters) => (playlistID) => {
		return getters.getPages().find(({PlaylistID}) => PlaylistID === playlistID);
	},

	getHotKeysRowsPerSoundCard: (state) => () => {
		return state.selectedPage.hotKeysRowsPerSoundCard;
	},

	getOutputType: (state) => (key) => {
		return state.outputType[key];
	},

	getGainHotkey: (state) => (key) => {
		return state.gain[key];
	},

	getHotkeyLoading: (state) => (sequenceNumber) => {
		return state.hotkeyLoading.find(h => h.sequenceNumber === sequenceNumber)?.loading;
	}
}

const mutations = {
	setPages(state, pages) {
		state.pages.push(...pages.map(p => {
			return {...p, locked: false};
		}));
	},

	setSelectedPageId(state, pageId) {
		state.selectedPageId = pageId;

		let page = state.pages.find(p => p.PlaylistID === pageId);
		state.selectedPage = {...defaultPageState(), ...page};
	},

	setPageHotKeys(state, {pageId, hotKeys}) {
		pageId = pageId ?? state.selectedPageId;
		const page = state.pages.find(p => p.PlaylistID === pageId);
		let pages = state.pages;
		pages.splice(pages.indexOf(page), 1, {...page, HotKeys: {data: hotKeys, loaded: true}});

		state.selectedPage = {...state.selectedPage, HotKeys: {data: hotKeys, loaded: true}};
	},

	setHotKeysRowsPerSoundCard(state, {index, rows}) {
		let hotKeysRowsPerSoundCard = state.selectedPage.hotKeysRowsPerSoundCard;
		hotKeysRowsPerSoundCard[index] = rows;
		state.selectedPage = {...state.selectedPage, hotKeysRowsPerSoundCard: hotKeysRowsPerSoundCard};
	},

	setOutputType(state, {key, value}) {
		state.outputType[key] = value;
	},

	setGainHotkey(state, {key, value}) {
		state.gain[key] = value;
	},

	resetHotKeysRowsPerSoundCard(state) {
		state.selectedPage.hotKeysRowsPerSoundCard = {};
	},

	addPage(state, page) {
		state.pages.push(page);
	},

	updatePage(state, page) {
		let pageIndex = state.pages.findIndex(p => p.PlaylistID === page.PlaylistID);
		state.pages.splice(pageIndex, 1, {...state.selectedPage, ...page});
		state.selectedPage = {...state.selectedPage, ...page};
	},

	removePage(state, pageId) {
		let index = state.pages.findIndex(p => p.PlaylistID === pageId);
		state.pages.splice(index, 1);
	},

	toggleLockPage(state, locked) {
		let page = state.pages.find(p => p.PlaylistID === state.selectedPageId);
		state.pages.splice(
			state.pages.indexOf(page),
			1,
			{...page, locked: locked}
		);
		state.selectedPage.locked = locked;
	},

	addHotkeyRequest(state, newHotKey) {
		let hotKeysEvents = state.selectedPage.HotKeys.data.Events;
		const existingHotKey = hotKeysEvents.find(
			e => e.EventID === newHotKey.EventID || e.SequenceNumber === newHotKey.SequenceNumber
		);
		if (existingHotKey) {
			hotKeysEvents.splice(hotKeysEvents.indexOf(existingHotKey), 1, {...newHotKey, loading: true});
		}
		else {
			hotKeysEvents.push({...newHotKey, loading: true});
		}
	},

	addHotkey(state, hotKeyEvent) {
		let hotKeysEvents = state.selectedPage.HotKeys.data.Events;
		let existingHotKey = hotKeysEvents.find(
			e => e.EventID === hotKeyEvent.EventID || e.SequenceNumber === hotKeyEvent.SequenceNumber
		);
		if (existingHotKey) {
			hotKeysEvents.splice(
				hotKeysEvents.indexOf(existingHotKey),
				1,
				{..._.merge(existingHotKey, hotKeyEvent), loading: false}
			);
		}
		else {
			hotKeysEvents.push({...hotKeyEvent, loading: false});
		}
	},

	addHotkeyAsset(state, {hotkey, asset}) {
		let hotKeyEvent = state.selectedPage.HotKeys.data.Events?.find(e => e.EventID === hotkey.EventID);
		if (hotKeyEvent) {
			hotKeyEvent.Asset = {...hotKeyEvent.Asset, ...asset, loading: false};
		}
	},

	updateHotkeyRequest(state, hotKeyEventId) {
		let hotKeysEvents = state.selectedPage.HotKeys.data.Events || [];
		const hotkey = hotKeysEvents.find(({EventID}) => EventID === hotKeyEventId);

		if (hotkey) {
			hotkey.loading = true;
		}
	},

	updateHotkey(state, {hotkey, updatedData}) {
		_.extend(hotkey, {...updatedData, loading: false});
	},

	removeHotkeyByEventId(state, hotKeyEventId) {
		let hotKeysEvents = state.selectedPage.HotKeys.data.Events || [];
		const hotKey = hotKeysEvents.find(h => h.EventID === hotKeyEventId);

		const index = hotKeysEvents.indexOf(hotKey);
		if (index > -1) {
			hotKeysEvents.splice(index, 1);
		}
	},

	removeHotkeyBySequenceNumber(state, hotKeyEvent) {
		let hotKeysEvents = state.selectedPage.HotKeys.data.Events || [];
		const hotKey = hotKeysEvents.find(h => h.SequenceNumber === hotKeyEvent.SequenceNumber);

		const index = hotKeysEvents.indexOf(hotKey);
		if (index > -1) {
			hotKeysEvents.splice(index, 1);
		}
	},

	resetHotkeysPages(state) {
		Object.assign(state, {...defaultState(), selectedPage: defaultPageState()});
	},

	setHotkeyLoading(state, {sequenceNumber, loading}) {
		let hotkeyIndex = state.hotkeyLoading.findIndex(h => h.sequenceNumber === sequenceNumber);

		if (hotkeyIndex > -1) {
			state.hotkeyLoading.splice(hotkeyIndex, 1);
		}
		else {
			state.hotkeyLoading.push({
				sequenceNumber,
				loading
			});
		}
	}
};

const actions = {
	getHotKeysPages({commit}, stationID) {
		commit("resetHotkeysPages");
		HotKeysApi.getPages(stationID).then(response => {
			const pages = response.data;
			commit("setPages", pages.map(d => ({...d, station: true})));
			if (pages.length) {
				commit("setSelectedPageId", pages[0].PlaylistID);
			}
			else {
				commit("setPageHotKeys", {hotKeys: []});
			}
		}).catch(error => {
			console.log(error);
		});
	},

	getHotKeysBySelectedPage({state, commit, dispatch}) {
		if (state.selectedPageId) {
			PlaylistApi.getPlaylistById(state.selectedPageId).then(response => {
				let hotKeysEvents = response.data.Events;

				hotKeysEvents.forEach(h => {
					h.Asset = {
						loading: true,
						AudioUrl: null
					}
				});

				commit("setPageHotKeys", {hotKeys: response.data});

				if (hotKeysEvents.length > 0) {
					AssetApi.list(_.compact(_.map(hotKeysEvents, "AssetID"))).then(response => {
						const assets = response.data;

						hotKeysEvents.forEach(hotkey => {
							const asset = assets.find(a => a.AssetID === hotkey.AssetID);
							commit("addHotkeyAsset", {hotkey, asset});

							// Subscribe to the Asset and StationAsset topics
							if (asset) {
								dispatch("assetSubscription", {assetID: asset.AssetID});
								dispatch(
									"stationAssetSubscription",
									{assetID: asset.AssetID, stationID: hotkey.StationID}
								);
								commit("addAssetHotkeySubscription", {assetID: hotkey.AssetID, hotkey});
							}
						});
					});
				}
			}).catch(error => {
				console.log(error);
			});
		}
	},

	createPage({commit}, payload) {
		return HotKeysApi.createPage(payload).then(response => {
			commit("addPage", {...response.data, station: true});
			commit("setSelectedPageId", response.data.PlaylistID);

			return response;
		}).catch(error => {
			console.log(error);
			return error.response;
		});
	},

	updatePage({commit}, payload) {
		return HotKeysApi.updatePage(payload).then(response => {
			commit("updatePage", {...payload, ...response.data});

			return response;
		}).catch(error => {
			console.log(error);
			return error.response;
		});
	},

	deletePage({state, commit}, pageId) {
		return HotKeysApi.deletePage(pageId).then(response => {
			commit("removePage", pageId);
			commit("setSelectedPageId", state.pages.length ? state.pages[0].PlaylistID : null);

			return response;
		}).catch(error => {
			console.log(error);
			return error.response;
		});
	},

	addHotKey({commit}, payload) {
		commit("addHotkeyRequest", payload);
		return HotKeysApi.addHotKey(_.omit(payload, "Asset"))
						 .catch(error => {
							 // Remove the hotkey placeholder
							 commit("removeHotkeyBySequenceNumber", payload);
							 return error.response;
						 });
	},

	updateHotkeyDueToMessage({commit}, {hotkey, updatedData}) {
		commit("updateHotkey", {hotkey, updatedData});
		// Update event in assetsHotkeysSubscriptionsMap
		if (hotkey.AssetID) {
			commit("updateAssetHotkeySubscription", hotkey);
		}
	},

	updateHotKey({commit}, payload) {
		commit("updateHotkeyRequest", payload.hotKeyEvent.EventID);
		return HotKeysApi.patchHotKey(
							 payload.hotKeyEvent.EventID,
							 _.omit(payload.updatedData, ["row", "column"])		// if payload contains row and column we get 400 error
						 )
						 .then(response => response)
						 .catch(error => {
							 console.log(error);
							 return error.response;
						 });
	},

	deleteHotKey(store, hotKeyEvent) {
		return HotKeysApi.deleteHotKey(hotKeyEvent.EventID);
	},

	getHotKeyAssetAudioUrl({dispatch}, hotkey) {
		dispatch("getAssetAudioUrl", hotkey).then(response => {
			dispatch("updateHotkeyDueToMessage",{
					hotkey,
					updatedData: {
						Asset: {
							...hotkey.Asset,
							AudioUrl: response.data.url
						}
					}
				}
			);
		}).catch(error => {
			console.log(error);
		});
	}
};

export default {
	state,
	getters,
	mutations,
	actions
};
